import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { imgUrl } from 'src/actions/types';
import { Modal, Button } from 'react-bootstrap';
import { PDFViewer, Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer'; // <-- PDF IMPORTS
import { formatCurrency } from "../utils/currencyHelper";
// --- Import all necessary actions ---
import { listAllInvoices } from 'src/actions/invoiceActions';
import { getAllPatientProfiles } from 'src/actions/patientProfileActions';
import { getAllDoctors } from 'src/actions/doctorActions';

// Helper function to get the correct badge class based on status
const getBadgeClass = (status) => {
  switch (status) {
    case 'Paid':
      return 'paid-label';            // Custom CSS class for Paid status
    case 'Pending':
      return 'pending-label';         // Custom CSS class for Pending
    case 'Cancelled':
      return 'cancelled-label';       // Custom CSS class for Cancelled
    default:
      return 'secondary-label';       // Custom CSS fallback
  }
};
const paginationStyles = `
    .pagination-wrapper { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; margin-top: 20px; padding: 15px 0; border-top: 1px solid #dee2e6; }
    .pagination .page-item.active .page-link { background-color: #0084d9; border-color: #0084d9; color: white; }
    .pagination .page-link { color: #0084d9; padding: 0.5rem 0.75rem; border-radius: 4px; margin: 0 2px; }
    .dataTables_info { font-size: 0.9rem; color: #6c757d; }
`;
// Helper function to determine Payment Mode from TransactionId
const getPaymentMode = (transactionId) => {
    if (!transactionId) return 'N/A';
    if (transactionId.startsWith('clinic_')) return 'Offline';
    return 'Online';
};


// Helper function for styling the payment mode badge
const getPaymentModeBadgeClass = (transactionId) => {
  const mode = getPaymentMode(transactionId);
  switch (mode) {
    case 'Online':
      return 'online-label';           // Custom CSS for Online payment
    case 'Offline':
      return 'offline-label';          // Custom CSS for Offline payment
    default:
      return 'na-label';               // Custom CSS for N/A or others
  }
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
};

// --- PDF Document Component (Defined directly in this file) ---
const pdfStyles = StyleSheet.create({
    page: { backgroundColor: '#FFFFFF', padding: 30, fontFamily: 'Helvetica' },
    header: { flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 2, borderBottomColor: '#1a237e', paddingBottom: 10, marginBottom: 20 },
    headerColumn: { width: '48%' },
    title: { fontSize: 24, fontWeight: 'bold', color: '#1a237e', marginBottom: 10 },
    infoText: { fontSize: 11, color: '#424242', marginBottom: 3 },
    section: { marginBottom: 20 },
    sectionTitle: { fontSize: 14, fontWeight: 'bold', color: '#3f51b5', marginBottom: 8 },
    pageNumber: { position: 'absolute', fontSize: 10, bottom: 30, left: 0, right: 0, textAlign: 'center', color: 'grey' },
    table: { display: "table", width: "auto", borderStyle: "solid", borderWidth: 1, borderRightWidth: 0, borderBottomWidth: 0 },
    tableRow: { margin: "auto", flexDirection: "row" },
    tableColHeader: { width: "75%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, backgroundColor: '#f0f0f0', padding: 5 },
    tableCol: { width: "75%", borderStyle: "solid", borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 5 },
    tableCellHeader: { fontSize: 10, fontWeight: 'bold' },
    tableCell: { margin: "auto", fontSize: 10 },
    invoiceTotalSection: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 20, borderTopWidth: 1, paddingTop: 10 },
    invoiceTotalLabel: { fontSize: 12, fontWeight: 'bold', width: '75%', textAlign: 'right', paddingRight: 10 },
    invoiceTotalValue: { fontSize: 12, fontWeight: 'bold', width: '25%', textAlign: 'right' },
    vitalLabel: { fontWeight: 'bold' },
});

const InvoicePDF = ({ invoice ,selectedCurrency  }) => (
    <Document>
        <Page size="A4" style={pdfStyles.page}>
            <View style={pdfStyles.header}>
                <View style={pdfStyles.headerColumn}>
                    <Text style={pdfStyles.title}>Invoice</Text>
                    <Text style={pdfStyles.infoText}><Text style={pdfStyles.vitalLabel}>Invoice ID:</Text> INV-{invoice?.id}</Text>
                    <Text style={pdfStyles.infoText}><Text style={pdfStyles.vitalLabel}>Date:</Text> {formatDate(invoice?.date)}</Text>
                </View>
                <View style={[pdfStyles.headerColumn, { textAlign: 'right' }]}>
                    <Text style={pdfStyles.infoText}><Text style={pdfStyles.vitalLabel}>Status:</Text> {invoice?.Status}</Text>
                </View>
            </View>

            <View style={pdfStyles.header}>
                <View style={pdfStyles.headerColumn}>
                    <Text style={pdfStyles.sectionTitle}>Billed To</Text>
                    <Text style={pdfStyles.infoText}>{invoice?.patientDetails?.firstName} {invoice?.patientDetails?.lastName}</Text>
                    <Text style={pdfStyles.infoText}>Patient ID: {invoice?.patientDetails?.PatientID}</Text>
                </View>
                <View style={[pdfStyles.headerColumn, { textAlign: 'right' }]}>
                    <Text style={pdfStyles.sectionTitle}>Billed By</Text>
                    <Text style={pdfStyles.infoText}>Dr. {invoice?.doctorDetails?.firstName} {invoice?.doctorDetails?.lastName}</Text>
                </View>
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Summary</Text>
                <View style={pdfStyles.table}>
                    <View style={pdfStyles.tableRow}>
                        <View style={pdfStyles.tableColHeader}><Text style={pdfStyles.tableCellHeader}>Description</Text></View>
                        <View style={[pdfStyles.tableColHeader, { width: '25%' }]}><Text style={pdfStyles.tableCellHeader}>Amount</Text></View>
                    </View>
                    <View style={pdfStyles.tableRow}>
                        <View style={pdfStyles.tableCol}><Text style={pdfStyles.tableCell}>General Consultation</Text></View>
                        <View style={[pdfStyles.tableCol, { width: '25%' }]}><Text style={pdfStyles.tableCell}>₹{invoice?.TotalAmount}</Text></View>
                    </View>
                </View>
                <View style={pdfStyles.invoiceTotalSection}>
                    <Text style={pdfStyles.invoiceTotalLabel}>Total Amount:</Text>
                    <Text style={pdfStyles.invoiceTotalValue}>{formatCurrency(invoice?.TotalAmount, selectedCurrency)}</Text>
                </View>
            </View>

            <Text style={pdfStyles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
        </Page>
    </Document>
);


const TransactionsList = () => {
  const dispatch = useDispatch();

  const { loading: invoicesLoading, error, invoices } = useSelector((state) => state.invoice);
  const { loading: patientsLoading, profiles: patients } = useSelector((state) => state.patientProfile);
  const { loading: doctorsLoading, doctors } = useSelector((state) => state.doctorList);
  const { selectedCurrency } = useSelector((state) => state.currencyReducer);
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const isLoading = invoicesLoading || patientsLoading || doctorsLoading;



  useEffect(() => {
    dispatch(listAllInvoices());
    dispatch(getAllPatientProfiles());
    dispatch(getAllDoctors());
  }, [dispatch]);
  
  const enrichedInvoices = useMemo(() => {
    if (!invoices.length || !patients.length || !doctors.length) {
      return [];
    }
    const patientMap = new Map(patients.map(p => [p.PatientID, p]));
    const doctorMap = new Map(doctors.map(d => [d.hospitalID, d]));
    return invoices.map(invoice => ({
      ...invoice,
      patientDetails: patientMap.get(invoice.PatientID) || {},
      doctorDetails: doctorMap.get(invoice.hospitalID) || {},
    }));
  }, [invoices, patients, doctors]);
    const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = enrichedInvoices.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(enrichedInvoices.length / itemsPerPage);
  useEffect(() => {
        setCurrentPage(1);
    }, [itemsPerPage]);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

  const handleShowModal = (invoice) => {
    setSelectedInvoice(invoice);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInvoice(null);
  };
const renderItemsPerPageSelector = () => (
    <div className="dataTables_length">
        <label>
            Items: 
            <select 
                className="form-control form-control-sm ml-2"
                value={itemsPerPage}
                onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to page 1
                }}
                style={{ width: '70px', display: 'inline-block' }}
            >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
            </select>
        </label>
    </div>
);
const renderPagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    return (
        <div className="pagination-controls-wrapper">
            <div className="dataTables_info">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, enrichedInvoices.length)} of {enrichedInvoices.length} entries
            </div>
            <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
                </li>
                
                {pageNumbers.map(number => (
                    <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(number)}>{number}</button>
                    </li>
                ))}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                </li>
            </ul>
        </div>
    );
};
  return (
    
    <div>
       <style>{`
        /* Pagination Main Wrapper */
        .pagination-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            margin-top: 20px;
            padding: 15px 0;
            border-top: 1px solid #dee2e6;
        }

        .items-per-page-wrapper {
            flex: 0 0 auto;
        }

        .pagination-controls-wrapper {
            flex: 1 1 auto;
            display: flex;
            justify-content: flex-end;
            align-items: center;
        }

        /* Items per page styling */
        .dataTables_length label {
            display: inline-flex;
            align-items: center;
            font-weight: normal;
            margin-bottom: 0;
            color: #6c757d;
        }

        .dataTables_length select {
            margin-left: 10px;
            width: 70px;
            border-radius: 4px;
            border: 1px solid #ddd;
            padding: 5px;
            height: 35px;
        }

        /* Pagination List */
        .pagination {
            margin: 0;
            padding: 0;
            list-style: none;
            display: flex;
        }

        .pagination .page-item .page-link {
            color: #0084d9;
            padding: 8px 14px;
            border: 1px solid #dee2e6;
            background-color: #fff;
            margin: 0 2px;
            border-radius: 4px;
            text-decoration: none;
            font-size: 14px;
            transition: all 0.3s ease;
        }

        .pagination .page-item.active .page-link {
            background-color: #0084d9;
            border-color: #0084d9;
            color: white;
        }

        .pagination .page-item.disabled .page-link {
            color: #6c757d;
            pointer-events: none;
            background-color: #fff;
            border-color: #dee2e6;
            opacity: 0.6;
        }

        /* "Showing X to Y" Info Text */
        .dataTables_info {
            font-size: 0.9rem;
            color: #6c757d;
            margin-right: 20px;
        }

        /* Responsive Mobile View */
        @media (max-width: 768px) {
            .pagination-wrapper {
                flex-direction: column;
                align-items: flex-start;
            }
            .pagination-controls-wrapper {
                justify-content: flex-start;
                margin-top: 15px;
                width: 100%;
                overflow-x: auto;
            }
            .dataTables_info {
                margin-bottom: 10px;
                width: 100%;
                text-align: left;
            }
        }
    `}</style>
      <div className="page-wrapper ml-0">
        <div className="content-container">
          {/* Header */}
          <div className="page-header filter-bar rounded">
          <div className="row align-items-center">
            <div className="col">
              <h4 className="page-title">Transactions</h4>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/doctor/dashboard">Admin Dashboard</Link>
                </li>
                <li className="breadcrumb-item active">Transactions</li>
              </ul>
            </div>
          </div>
        </div>

          {/* Table */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="datatable table table-hover table-center mb-0">
                        <thead>
                            <tr>
                                <th>Invoice ID</th>
                                <th>Appointment ID</th>

                                <th>Transaction ID</th>
                                <th>Date</th>
                                <th>Patient</th>
                                <th>Doctor</th>
                                <th>Payment Mode</th>
                                <th>Total Amount</th>
                                <th className="text-center">Status</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="9" className="text-center">Loading...</td></tr>
                            ) : error ? (
                                <tr><td colSpan="9" className="text-center text-danger">Error: {error}</td></tr>
                            ) : currentItems.length > 0 ? (
                            currentItems.map((item) => (
                                <tr key={item.id}>
                                    <td>#{item.id}</td>
                                    <td>{item.appointmentId}</td>
                                    <td style={{ wordBreak: 'break-all' }}>
                                        <Link to={`/admin/Transactionreport/${item.TransactionId}`}>{item.TransactionId || 'N/A'}</Link>
                                    </td>
                                    <td>{formatDate(item.date)}</td>
                                    <td>
                                        <h2 className="table-avatar">
                                            <Link to={`/admin/profile/${item.PatientID}`} className="avatar avatar-sm mr-2">
                                                <img className="avatar-img rounded-circle" src={item.patientDetails.uploadPhoto ? `${imgUrl}${item.patientDetails.uploadPhoto}` : '/assets/img/patients/patient.jpg'} alt="Patient" />
                                            </Link>
                                            <Link to={`/admin/profile/${item.PatientID}`}>{item.patientDetails.firstName || 'Unknown'} {item.patientDetails.lastName || 'Patient'}</Link>
                                        </h2>
                                    </td>
                                    <td>
                                        <h2 className="table-avatar">
                                            <Link to={`/admin/doctor-profile/${item.hospitalID}`} className="avatar avatar-sm mr-2">
                                                <img className="avatar-img rounded-circle" src={item.doctorDetails.uploadPhoto ? `${imgUrl}${item.doctorDetails.uploadPhoto}` : '/assets/img/doctors/doctor-thumb-01.jpg'} alt="Doctor"/>
                                            </Link>
                                            <Link to={`/admin/doctor-profile/${item.hospitalID}`}>Dr. {item.doctorDetails.firstName || 'Unknown'} {item.doctorDetails.lastName || 'Doctor'}</Link>
                                        </h2>
                                    </td>
                                    <td className="text-center">
                                      <span className={`badge badge-pill ${getPaymentModeBadgeClass(item.TransactionId)}`}>
                                        {getPaymentMode(item.TransactionId)}
                                      </span>
                                    </td>
                                    <td>{formatCurrency(item.TotalAmount, selectedCurrency)}</td>
                                    <td className="text-center">
                                        <span className={`badge badge-pill ${getBadgeClass(item.Status)}`}>{item.Status}</span>
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => handleShowModal(item)} className="i-btn-view">
                                            
                                        </button>
                                    </td>
                                </tr>
                            ))
                            ) : (
                                <tr><td colSpan="9" className="text-center">No transactions found.</td></tr>
                            )}
                        </tbody>
                    </table>
                  </div>
                {enrichedInvoices.length > 0 && (
    <div className="pagination-wrapper">
        <div className="items-per-page-wrapper">
            {renderItemsPerPageSelector()}
        </div>
        <div className="pagination-controls-wrapper">
            {renderPagination()}
        </div>
    </div>
)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- NEW: PDF Viewer Modal --- */}
      {selectedInvoice && (
        <Modal show={showModal} onHide={handleCloseModal} centered size="xl">
          <Modal.Header className=' bg-info'closeButton closeVariant="white">
            <Modal.Title>Invoice Viewer: #{selectedInvoice.id}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: '80vh' }}>
            <PDFViewer width="100%" height="100%">
              <InvoicePDF invoice={selectedInvoice} selectedCurrency={selectedCurrency}/>
            </PDFViewer>
          </Modal.Body>
          <Modal.Footer>
            <button className='i-btn-cancel' onClick={handleCloseModal}>
              
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default TransactionsList;